(() => {
  const ROOT_SELECTOR = '[data-fl-root]';

  function money(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(Number(cents || 0) / 100);
  }

  function emit(name, payload = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({event: name, ...payload});
  }

  function setText(root, selector, value) {
    root.querySelectorAll(selector).forEach((node) => { node.textContent = value; });
  }

  function setup(root) {
    if (root.dataset.flInitialized === 'true') return;
    root.dataset.flInitialized = 'true';

    let variants = [];
    try {
      variants = JSON.parse(root.querySelector('[data-fl-variants]')?.textContent || '[]');
    } catch (error) {
      console.error('Unable to parse freight-ladder variants.', error);
    }

    const tierButtons = [...root.querySelectorAll('[data-fl-tier]')];
    const sizeButtons = [...root.querySelectorAll('[data-fl-size]')];
    const addButtons = [...root.querySelectorAll('[data-fl-add]')];
    const galleryFrame = root.querySelector('[data-fl-gallery-frame]');
    const heroImage = root.querySelector('[data-fl-hero-image]');
    const mainAddButton = root.querySelector('[data-fl-add="main"]');
    const sticky = root.querySelector('[data-fl-sticky]');
    const designTrack = root.querySelector('[data-fl-design-track]');
    const designGallery = root.querySelector('[data-fl-design-gallery]');
    const designSlides = [...root.querySelectorAll('[data-fl-design-slide]')];
    const designThumbs = [...root.querySelectorAll('[data-fl-design-thumb]')];
    const canPreview = root.querySelector('[data-fl-can-preview]');
    const canPreviewItems = [...root.querySelectorAll('[data-fl-can-preview-item]')];
    const galleryCans = [...root.querySelectorAll('[data-fl-gallery-can]')];
    const designPrevious = root.querySelector('[data-fl-design-prev]');
    const designNext = root.querySelector('[data-fl-design-next]');
    const errorBox = root.querySelector('[data-fl-error]');
    const selectionStatus = root.querySelector('[data-fl-selection-status]');
    const sizeStep = root.querySelector('.cs-step--size');
    const sizeGuidance = sizeStep?.querySelector('.cs-step__heading p');
    const defaultSizeGuidance = sizeGuidance?.textContent || '';
    let activeDesign = 'wtp';
    let designScrollFrame = null;
    let sizeConfirmed = false;
    const requestedVariantId = Number(new URLSearchParams(window.location.search).get('variant') || 0);
    const requestedVariant = variants.find((variant) => Number(variant.id) === requestedVariantId);
    const state = {
      package: requestedVariant?.package || tierButtons.find((button) => button.classList.contains('is-selected'))?.dataset.flPackage || tierButtons[0]?.dataset.flPackage,
      size: requestedVariant?.size || 'Large',
      variant: null,
      adding: false,
    };

    function findVariant(size = state.size) {
      return variants.find((variant) => variant.package === state.package && variant.size === size);
    }

    function selectedTierButton() {
      return tierButtons.find((button) => button.dataset.flPackage === state.package);
    }

    if (sticky && sticky.parentElement !== document.body) document.body.appendChild(sticky);

    if (sticky) {
      const updateStickyClearance = () => {
        document.documentElement.style.setProperty('--fl-sticky-height', `${Math.ceil(sticky.getBoundingClientRect().height)}px`);
      };
      updateStickyClearance();
      if ('ResizeObserver' in window) new ResizeObserver(updateStickyClearance).observe(sticky);
      window.addEventListener('resize', updateStickyClearance, {passive: true});
    }

    function visibleDesignSlides() {
      return designSlides.filter((slide) => !slide.hidden);
    }

    function updateDesignControls(design) {
      activeDesign = design;
      designSlides.forEach((slide) => slide.classList.toggle('is-active', slide.dataset.flDesignSlide === design));
      designThumbs.forEach((thumb) => {
        const selected = thumb.dataset.flDesignThumb === design;
        thumb.classList.toggle('is-active', selected);
        thumb.setAttribute('aria-pressed', String(selected));
      });
    }

    function setDesign(design, {scroll = true, smooth = true} = {}) {
      const slide = visibleDesignSlides().find((candidate) => candidate.dataset.flDesignSlide === design);
      if (!slide || !designTrack) return;
      updateDesignControls(design);
      if (!scroll) return;
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      designTrack.scrollTo({left: slide.offsetLeft, behavior: smooth && !reduceMotion ? 'smooth' : 'auto'});
    }

    function moveDesign(direction) {
      const visible = visibleDesignSlides();
      if (!visible.length) return;
      const current = Math.max(0, visible.findIndex((slide) => slide.dataset.flDesignSlide === activeDesign));
      setDesign(visible[(current + direction + visible.length) % visible.length].dataset.flDesignSlide);
    }

    function syncDesignGallery(canCount) {
      if (canPreview) canPreview.dataset.flPreviewCount = String(canCount);
      canPreviewItems.forEach((item) => { item.hidden = Number(item.dataset.flCanMin || 1) > canCount; });
      setText(root, '[data-fl-gallery-can-heading]', `${canCount} of 4 cans included`);
      galleryCans.forEach((item) => {
        const included = Number(item.dataset.flCanMin || 1) <= canCount;
        item.classList.toggle('is-not-included', !included);
        const status = item.querySelector('[data-fl-gallery-can-status]');
        if (status) status.textContent = included ? 'Included' : `Unlocked with ${item.dataset.flUnlockTier}`;
      });
      designGallery?.classList.toggle('is-single-can', canCount === 1);
      if (designTrack) {
        designTrack.tabIndex = canCount === 1 ? -1 : 0;
        designTrack.setAttribute('aria-label', canCount === 1
          ? 'Included We The People engraved can design.'
          : 'Included engraved can designs. Swipe or use the arrow buttons to browse.');
      }
      designSlides.forEach((slide) => { slide.hidden = Number(slide.dataset.flCanMin || 1) > canCount; });
      designThumbs.forEach((thumb) => { thumb.hidden = Number(thumb.dataset.flCanMin || 1) > canCount; });
      if (!visibleDesignSlides().some((slide) => slide.dataset.flDesignSlide === activeDesign)) activeDesign = 'wtp';
      window.requestAnimationFrame(() => setDesign(activeDesign, {smooth: false}));
    }

    function syncAvailability() {
      sizeButtons.forEach((button) => {
        const variant = findVariant(button.dataset.flSize);
        button.disabled = !variant || !variant.available;
      });
      if (!findVariant()?.available) {
        const fallback = sizeButtons.find((button) => findVariant(button.dataset.flSize)?.available);
        if (fallback) {
          state.size = fallback.dataset.flSize;
          sizeConfirmed = false;
        }
      }
    }

    function sync() {
      syncAvailability();
      state.variant = findVariant();
      const tierAvailable = variants.some((variant) => variant.package === state.package && variant.available);
      const tier = selectedTierButton();
      const price = money(state.variant?.price || 0);
      const targetCount = Number(tier?.dataset.flTargets || 0);
      const splatterCount = Number(tier?.dataset.flSplatter || 0);
      const silhouetteCount = Number(tier?.dataset.flSilhouette || 0);
      const boxCount = Number(tier?.dataset.flBoxes || 0);
      const canCount = Number(tier?.dataset.flCans || 0);
      const rounds556 = tier?.getAttribute('data-fl-556') || '0';
      const rounds9mm = tier?.getAttribute('data-fl-9mm') || '0';
      const totalRounds = Number(rounds556.replace(/,/g, '')) + Number(rounds9mm.replace(/,/g, ''));
      const nextHero = tier?.dataset.flHero;

      tierButtons.forEach((button) => {
        const selected = button.dataset.flPackage === state.package;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', String(selected));
        button.tabIndex = selected ? 0 : -1;
      });
      const firstAvailableSize = sizeButtons.find((button) => !button.disabled);
      sizeButtons.forEach((button) => {
        const selected = sizeConfirmed && button.dataset.flSize === state.size;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', String(selected));
        button.tabIndex = selected || (!sizeConfirmed && button === firstAvailableSize) ? 0 : -1;
      });

      setText(root, '[data-fl-summary-title]', tier?.dataset.flLabel || 'Ammo Vault');
      setText(root, '[data-fl-summary-price]', price);
      setText(root, '[data-fl-summary-556]', `${rounds556} rounds`);
      setText(root, '[data-fl-summary-9mm]', `${rounds9mm} rounds`);
      setText(root, '[data-fl-summary-targets]', tier?.dataset.flTargets || '0');
      setText(root, '[data-fl-summary-splatter]', String(splatterCount));
      setText(root, '[data-fl-summary-silhouette]', String(silhouetteCount));
      setText(root, '[data-fl-summary-boxes]', tier?.dataset.flBoxes || '0');
      setText(root, '[data-fl-summary-cans]', tier?.dataset.flCans || '0');
      setText(root, '[data-fl-summary-shirts]', sizeConfirmed ? `4 × ${state.size}` : 'Choose one size');
      setText(root, '[data-fl-add-label]', !tierAvailable ? 'This vault is sold out' : sizeConfirmed ? `Get My Ammo — ${price}` : 'Choose shirt size');
      if (sticky) {
        setText(sticky, '[data-fl-sticky-price]', price);
        setText(sticky, '[data-fl-sticky-contents]', `${new Intl.NumberFormat('en-US').format(totalRounds)} rounds · 4 shirts + gear`);
        setText(sticky, '[data-fl-add-label]', !tierAvailable ? 'This vault is sold out' : sizeConfirmed ? 'Get My Ammo' : 'Choose shirt size');
      }
      setText(root, '[data-fl-target-count]', String(targetCount));
      setText(root, '[data-fl-splatter-count]', String(splatterCount));
      setText(root, '[data-fl-silhouette-count]', String(silhouetteCount));
      setText(root, '[data-fl-storage-count]', String(boxCount));
      setText(root, '[data-fl-storage-colors]', boxCount === 10 ? '5 white + 5 khaki boxes' : '5 white boxes');
      setText(root, '[data-fl-can-count]', String(canCount));
      setText(root, '[data-fl-can-preview-heading]', canCount === 1
        ? '1 engraved can · We The People'
        : `${canCount} engraved cans · ${tier?.dataset.flLabel || 'Your vault'}`);
      setText(root, '[data-fl-can-heading-ending]', canCount === 1 ? 'can is included.' : 'cans are included.');
      setText(root, '[data-fl-can-storage-noun]', canCount === 1 ? 'engraved .50-cal can' : 'engraved .50-cal cans');
      setText(root, '[data-fl-design-count]', `${canCount} ${canCount === 1 ? 'DESIGN' : 'DESIGNS'} INCLUDED`);
      setText(root, '[data-fl-can-kicker]', canCount === 1 ? 'THE SINGLE-CAN LOADOUT' : canCount === 2 ? 'THE TWO-CAN LOADOUT' : 'THE COMPLETE FOUR-CAN LOADOUT');
      setText(root, '[data-fl-can-names]', canCount === 1
        ? 'We The People'
        : canCount === 2
          ? 'We The People + Land of the Free'
          : "We The People + Land of the Free + Don't Tread on Me + Join, or Die");
      setText(root, '[data-fl-can-lead]', canCount === 1
        ? 'The We The People steel can gives your range-day gear a durable home after the order arrives.'
        : canCount === 2
          ? 'We The People and Land of the Free steel cans give your range-day gear durable storage after the order arrives.'
          : "The complete We The People, Land of the Free, Don't Tread on Me and Join, or Die collection gives your range-day gear four engraved steel storage options.");

      if (heroImage && nextHero && heroImage.src !== nextHero) {
        galleryFrame?.classList.add('is-changing');
        heroImage.src = nextHero;
        heroImage.alt = tier?.dataset.flHeroAlt || '';
        window.setTimeout(() => galleryFrame?.classList.remove('is-changing'), 180);
      }
      syncDesignGallery(canCount);

      addButtons.forEach((button) => {
        button.disabled = state.adding || !tierAvailable || !state.variant?.available;
      });
      if (errorBox) {
        if (!tierAvailable) {
          errorBox.dataset.flAvailabilityError = 'true';
          errorBox.hidden = false;
          errorBox.textContent = 'This vault is sold out in every shirt size. Choose another reserve to continue.';
        } else if (errorBox.dataset.flAvailabilityError === 'true') {
          delete errorBox.dataset.flAvailabilityError;
          errorBox.hidden = true;
          errorBox.textContent = '';
        }
      }
      root.classList.add('is-price-ready');
    }

    function guideToSize() {
      if (sizeGuidance) {
        sizeGuidance.setAttribute('aria-live', 'polite');
        sizeGuidance.textContent = 'Choose one shirt size to continue. All four shirts arrive in that size.';
      }
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      sizeStep?.scrollIntoView({behavior: reduceMotion ? 'auto' : 'smooth', block: 'center'});
      sizeButtons.find((button) => !button.disabled)?.focus({preventScroll: true});
    }

    function guideBackToBuy() {
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const behavior = reduceMotion ? 'auto' : 'smooth';
      const tierAvailable = variants.some((variant) => variant.package === state.package && variant.available);
      if (!tierAvailable) {
        root.querySelector('.cs-step--tiers')?.scrollIntoView({behavior, block: 'center'});
        tierButtons.find((button) => variants.some((variant) => variant.package === button.dataset.flPackage && variant.available))?.focus({preventScroll: true});
      } else if (!sizeConfirmed) {
        guideToSize();
      } else {
        mainAddButton?.scrollIntoView({behavior, block: 'center'});
        mainAddButton?.focus({preventScroll: true});
      }
    }

    async function addToCart(source) {
      if (!sizeConfirmed) {
        guideToSize();
        return;
      }
      if (state.adding || !state.variant?.available) return;
      state.adding = true;
      addButtons.forEach((button) => { button.disabled = true; });
      if (errorBox) errorBox.hidden = true;
      setText(root, '[data-fl-add-label]', 'Adding your vault…');
      if (sticky) setText(sticky, '[data-fl-add-label]', 'Adding your vault…');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({
            items: [{
              id: state.variant.id,
              quantity: 1,
              properties: {
                _pa_offer: root.dataset.offer,
                _pa_offer_version: root.dataset.offerVersion,
                _pa_experiment: root.dataset.experiment,
                _pa_cell: root.dataset.cell,
                _pa_variant_sku: state.variant.sku,
                ...(root.dataset.freeShipping === 'true' ? {
                  _pa_shipping_included: 'true',
                  _pa_skip_shipping_insurance: 'true',
                } : {}),
              },
            }],
          }),
        });
        if (!response.ok) throw new Error(`Cart returned ${response.status}.`);
        emit('pa_freight_test_add_to_cart', {
          experiment: root.dataset.experiment,
          cell: root.dataset.cell,
          source,
          tier: selectedTierButton()?.dataset.flTier,
          shirt_size: state.size,
          variant_id: state.variant.id,
          sku: state.variant.sku,
          value: Number(state.variant.price || 0) / 100,
          currency: 'USD',
        });
        window.location.assign(root.dataset.checkoutUrl || '/checkout');
      } catch (error) {
        state.adding = false;
        if (errorBox) {
          errorBox.hidden = false;
          errorBox.textContent = 'We could not add this vault. Please refresh and try again.';
        }
        sync();
      }
    }

    tierButtons.forEach((button) => button.addEventListener('click', () => {
      state.package = button.dataset.flPackage;
      sync();
      if (selectionStatus) {
        const rounds = Number((button.getAttribute('data-fl-556') || '0').replace(/,/g, ''))
          + Number((button.getAttribute('data-fl-9mm') || '0').replace(/,/g, ''));
        selectionStatus.textContent = `${button.dataset.flLabel} selected: ${new Intl.NumberFormat('en-US').format(rounds)} rounds, ${button.dataset.flCans} engraved ${button.dataset.flCans === '1' ? 'can' : 'cans'}, ${money(state.variant?.price || 0)}${root.dataset.freeShipping === 'true' ? ' delivered where eligible' : ' plus shipping'}. ${sizeConfirmed ? `Four shirts in ${state.size}.` : 'Choose one shirt size to continue.'}`;
      }
      emit('pa_freight_test_tier_select', {
        experiment: root.dataset.experiment,
        cell: root.dataset.cell,
        tier: button.dataset.flTier,
      });
    }));
    sizeButtons.forEach((button) => button.addEventListener('click', () => {
      if (button.disabled) return;
      state.size = button.dataset.flSize;
      sizeConfirmed = true;
      if (sizeGuidance) sizeGuidance.textContent = defaultSizeGuidance;
      sync();
      if (selectionStatus) selectionStatus.textContent = `All four shirts set to ${state.size}.`;
    }));
    function enableRadioArrowKeys(buttons) {
      buttons.forEach((button, index) => button.addEventListener('keydown', (event) => {
        const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1
          : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
        if (!direction) return;
        event.preventDefault();
        for (let step = 1; step <= buttons.length; step += 1) {
          const next = buttons[(index + direction * step + buttons.length) % buttons.length];
          if (next.disabled) continue;
          next.click();
          next.focus({preventScroll: true});
          break;
        }
      }));
    }
    enableRadioArrowKeys(tierButtons);
    enableRadioArrowKeys(sizeButtons);
    designThumbs.forEach((thumb) => thumb.addEventListener('click', () => setDesign(thumb.dataset.flDesignThumb)));
    designPrevious?.addEventListener('click', () => moveDesign(-1));
    designNext?.addEventListener('click', () => moveDesign(1));
    designTrack?.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      moveDesign(event.key === 'ArrowLeft' ? -1 : 1);
    });
    designTrack?.addEventListener('scroll', () => {
      if (designScrollFrame !== null) window.cancelAnimationFrame(designScrollFrame);
      designScrollFrame = window.requestAnimationFrame(() => {
        const nearest = visibleDesignSlides().reduce((winner, slide) => {
          if (!winner) return slide;
          return Math.abs(slide.offsetLeft - designTrack.scrollLeft) < Math.abs(winner.offsetLeft - designTrack.scrollLeft) ? slide : winner;
        }, null);
        if (nearest) updateDesignControls(nearest.dataset.flDesignSlide);
        designScrollFrame = null;
      });
    }, {passive: true});
    root.querySelectorAll('[data-fl-scroll-to-buy]').forEach((button) => button.addEventListener('click', guideBackToBuy));
    addButtons.forEach((button) => button.addEventListener('click', () => addToCart(button.dataset.flAdd || 'main')));

    if (sticky && mainAddButton && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        const visible = !entries[0]?.isIntersecting;
        sticky.classList.toggle('is-visible', visible);
        sticky.setAttribute('aria-hidden', String(!visible));
        sticky.inert = !visible;
      }, {threshold: 0.1});
      observer.observe(mainAddButton);
    }

    sync();
    emit('pa_freight_test_impression', {
      experiment: root.dataset.experiment,
      cell: root.dataset.cell,
      product_id: root.dataset.productId,
    });
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(setup);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
